import type { ExternalUserFormData } from "@/schemas/users/external-user";
import { formatPhoneNumber } from "@/utils/format";
import { cn } from "@/utils/theme";
import {
  Avatar,
  AvatarFallback,
  Button,
  Heading,
  Icon,
  Switch,
} from "@elaniintc/goes-design-system";
import { useFormContext } from "react-hook-form";
import IconWrapper from "../components/icon-wrapper";
import Insolvencies from "../actions/insolvencies";

interface Props {
  selectedUserType: {
    label: string;
    value: string;
  } | null;
  isUserActive: boolean;
  isUserBlocked?: boolean;
  toggleUserActivation: (isActive: boolean) => void;
  onResetPassword: () => void;
  onUnblockUser?: () => void;
  userId: string;
}

const ExternalUserDetails = ({
  selectedUserType,
  isUserActive,
  isUserBlocked = false,
  toggleUserActivation,
  onResetPassword,
  onUnblockUser,
  userId,
}: Props) => {
  const { watch } = useFormContext<ExternalUserFormData>();
  const data = watch();

  const showLastName = data.document_type !== "NIT";
  const isLegalPerson = data.document_type === "NIT";

  const fullName = isLegalPerson
    ? data.commercial_name
      ? `${data.first_name || ""} (${data.commercial_name})`.trim()
      : data.first_name || ""
    : showLastName
      ? `${data.first_name || ""} ${data.last_name || ""}`.trim()
      : data.first_name || "";

  return (
    <div className="p-4">
      <div className="flex flex-col gap-4 pb-4">
        <Avatar size="xl">
          <AvatarFallback color="action">
            {data.first_name?.charAt(0) || data.last_name?.charAt(0) || ""}
          </AvatarFallback>
        </Avatar>
        <div>
          <Heading
            variant="h5"
            className={cn("text-on-disabled", fullName && "text-heading")}
          >
            {fullName ||
              (isLegalPerson
                ? "Razón social (Nombre comercial)"
                : "Nombre y apellido")}
          </Heading>
          <p
            className={cn(
              "text-base leading-3xl text-body-quiet",
              selectedUserType && "text-on-disabled",
            )}
          >
            {selectedUserType?.label || "Tipo de usuario"}
          </p>
        </div>
      </div>
      <div className="w-full h-px border-t border-neutral-quiet" />
      <div className="flex flex-col gap-2 py-4">
        <div className="flex items-center gap-3">
          <IconWrapper>
            <Icon type="far" name="id-card" />
          </IconWrapper>
          <div className="flex flex-col gap-1">
            <span
              className={cn(
                "text-body font-normal",
                !data.document_number && "text-on-disabled",
              )}
              data-testid="profile-document"
            >
              {data.document_number || "Número de documento"}
            </span>
          </div>
        </div>
        {data.document_type !== "NIT" && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <IconWrapper>
                <Icon type="far" name="birthday-cake" />
              </IconWrapper>
              <span
                className={cn(
                  "text-body font-normal",
                  !data.birthdate && "text-on-disabled",
                )}
                data-testid="profile-birthdate"
              >
                {data.birthdate
                  ? (() => {
                      try {
                        const date = new Date(data.birthdate);
                        if (!isNaN(date.getTime())) {
                          return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
                        }
                      } catch {
                        return data.birthdate;
                      }
                      return data.birthdate;
                    })()
                  : "Fecha de nacimiento"}
              </span>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <IconWrapper>
              <Icon type="far" name="envelope" />
            </IconWrapper>
            <span
              className={cn(
                "text-body font-normal",
                !data.email && "text-on-disabled",
              )}
              data-testid="profile-email"
            >
              {data.email || "Correo electrónico"}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <IconWrapper>
              <Icon type="far" name="phone" />
            </IconWrapper>
            <span
              className={cn(
                "text-body font-normal",
                !data.phone && "text-on-disabled",
              )}
              data-testid="profile-phone"
            >
              {data.phone
                ? formatPhoneNumber(data.phone)
                : "Número de teléfono"}
            </span>
          </div>
        </div>
      </div>
      <div className="w-full">
        <div className="flex flex-col justify-between">
          <Insolvencies userName={fullName || "Usuario"} userId={userId} />
        </div>
      </div>
      <div className="w-full h-px" />
      <div className="flex flex-col gap-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <IconWrapper>
              <Icon type="far" name="user-check" />
            </IconWrapper>
            <span className="text-body-loud text-base leading-3xl font-semibold">
              Estado
            </span>
          </div>
          <div className="flex items-center gap-2.5">
            <label
              htmlFor="profile-status-switch"
              className="text-body text-base leading-3xl"
            >
              {isUserActive ? "Activo" : "Inactivo"}
            </label>
            <Switch
              checked={isUserActive}
              id="profile-status-switch"
              onCheckedChange={(checked) => {
                toggleUserActivation(checked);
              }}
              data-testid="profile-status-switch"
              className="!border-none"
            />
          </div>
        </div>
        <Button
          size="default"
          variant="tertiary"
          className="w-full"
          type="button"
          onClick={onResetPassword}
        >
          Restablecer contraseña
          <Icon type="far" name="lock" className="text-current" />
        </Button>
        <Button
          size="default"
          variant="tertiary"
          className="w-full"
          type="button"
          disabled={!isUserBlocked}
          onClick={onUnblockUser}
        >
          Desbloquear usuario
          <Icon type="far" name="user-unlock" className="text-current" />
        </Button>
      </div>
    </div>
  );
};

export default ExternalUserDetails;
